<template>
  <article
    v-if="!component.editorialType"
    :id="component.uid"
    :qaid="componentPrefix"
    :class="[divClass, alignment, `editorial-large w-full transition duration-300`]"
  >
    <div class="editorial-large__wrap">
      <a v-if="component.editorialImage" :href="linkUrl">
        <div class="editorial-large__image-wrap">
          <image-component
            :qaid="`${componentPrefix}-image`"
            :asset-id="component.editorialImage"
            :css-class="`editorial-large__image`"
            transformation="limit"
            aspect-ratio="4x3"
            :alt-text="getAltText"
          />
        </div>
      </a>

      <a
        v-if="component.editorialAction"
        :id="component.editorialAction.uid"
        :href="linkUrl"
        v-bind="dynamicAttrs"
        :class="[component.editorialAction.trekLinkStyle]"
      >
        <h3 :qaid="`${componentPrefix}-header`" class="header-medium editorial-large__header">
          {{ component.editorialTitle }}
        </h3>
      </a>

      <a
        v-if="component.editorialAction"
        :id="`${component.editorialAction.uid}-text`"
        :href="linkUrl"
        :class="linkClasses"
      >
        {{ component.editorialAction.linkName }}
      </a>

      <p v-if="component.content" :qaid="`${componentPrefix}-body`" class="editorial-large__text text-weak">
        {{ component.content }}
      </p>
    </div>
  </article>
  <article
    v-else-if="component.editorialType === typeMap.large"
    :id="component.uid"
    :qaid="componentPrefix"
    :class="[divClass, alignment, `editorial-large w-full transition duration-300`]"
  >
    <div v-if="component.editorialImage" class="editorial-large__wrap">
      <a :href="linkUrl">
        <div class="editorial-large__image-wrap">
          <image-component
            :qaid="`${componentPrefix}-image`"
            :asset-id="component.editorialImage"
            :css-class="`editorial-large__image`"
            transformation="limit"
            aspect-ratio="4x3"
            :alt-text="getAltText"
          />
        </div>
      </a>
    </div>
    <div class="editorial-large__content">
      <a
        v-if="component.editorialAction"
        :id="component.editorialAction.uid"
        :href="linkUrl"
        v-bind="dynamicAttrs"
        :class="[component.editorialAction.trekLinkStyle]"
      >
        <h3 :qaid="`${componentPrefix}-header`" class="header-medium editorial-large__header linked">
          {{ component.editorialTitle }}
        </h3>
      </a>

      <p v-if="component.content" :qaid="`${componentPrefix}-body`" class="editorial-large__text text-weak">
        {{ component.content }}
      </p>

      <a
        v-if="component.editorialAction"
        :id="`${component.editorialAction.uid}-text`"
        :href="linkUrl"
        :class="linkClasses"
      >
        {{ component.editorialAction.linkName }}
      </a>
    </div>
  </article>
  <article
    v-else-if="component.editorialType === typeMap.medium"
    :id="component.uid"
    :qaid="componentPrefix"
    :class="[divClass, alignment, `editorial-medium w-full transition duration-300`]"
  >
    <div class="editorial-medium__wrap">
      <a v-if="component.editorialImage" :href="linkUrl">
        <div :class="`editorial-medium__image-wrap`">
          <image-component
            :qaid="`${componentPrefix}-image`"
            :asset-id="component.editorialImage"
            :css-class="`editorial-medium__image`"
            transformation="pad"
            aspect-ratio="4x3"
            :alt-text="getAltText"
          />
        </div>
      </a>
      <a
        v-if="component.editorialAction"
        :href="linkUrl"
        v-bind="dynamicAttrs"
        class="editorial-medium__button editorial__button--medium button-primary--expando"
      >
        {{ component.editorialAction.linkName }}
      </a>
    </div>
    <div class="editorial-medium__content">
      <a
        v-if="component.editorialAction"
        :href="linkUrl"
        v-bind="dynamicAttrs"
        :class="[component.editorialAction.trekLinkStyle]"
      >
        <h4 :qaid="`${componentPrefix}-header`" class="header-medium editorial-medium__header linked">
          {{ component.editorialTitle }}
        </h4>
      </a>
      <p v-if="component.content" :qaid="`${componentPrefix}-body`" class="editorial-medium__text text-weak">
        {{ component.content }}
      </p>
    </div>
  </article>
  <article
    v-else-if="smallEditorialTypes"
    :id="component.uid"
    :qaid="componentPrefix"
    :class="[divClass, alignment, `editorial-small w-full transition duration-300`]"
  >
    <div v-if="component.editorialImage" :class="['editorial-small__wrap', wrapperClass]">
      <a :href="linkUrl">
        <div class="editorial-small__image-wrap">
          <image-component
            :asset-id="component.editorialImage"
            css-class="editorial-small__image"
            transformation="pad"
            :aspect-ratio="aspectRatio"
            :alt-text="getAltText"
          />
        </div>
      </a>
    </div>
    <div class="editorial-small__content">
      <h2
        v-if="component.editorialTitle"
        :qaid="`${componentPrefix}-header`"
        class="editorial-small__header header-small"
        :class="{'mb-1': component.content}"
      >
        {{ component.editorialTitle }}
      </h2>

      <p v-if="component.content" :qaid="`${componentPrefix}-body`" class="editorial-small__text text-weak">
        {{ component.content }}
      </p>

      <a
        v-if="component.editorialAction"
        :id="`${component.editorialAction.uid}-text`"
        :href="linkUrl"
        v-bind="dynamicAttrs"
        :class="linkClasses"
      >
        {{ component.editorialAction.linkName }}
      </a>
    </div>
  </article>
</template>

<script>
import ImageComponent from '@/components/ImageComponent.vue';
import {createAltText} from '@/utils/image/image-helpers';
import {mapState} from 'vuex';

export default {
  components: {
    ImageComponent,
  },
  props: {
    component: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      componentPrefix: 'editorial-component',
      typeMap: {
        small: 'SMALL',
        small4x3: 'SMALL_4x3',
        medium: 'MEDIUM',
        large: 'LARGE',
      },
    };
  },
  computed: {
    ...mapState('backend', ['encodedContextPath']),

    alignment() {
      return {
        'text-left': this.component?.alignment === 'LEFT',
        'text-center': this.component?.alignment === 'CENTER',
        'text-right': this.component?.alignment === 'RIGHT',
      };
    },
    getAltText() {
      if (!this.component?.editorialTitle) {
        return createAltText(this.component?.editorialImage, 'Editorial text');
      }
      return this.component?.editorialTitle;
    },
    booleanConversions() {
      return {
        target: this.component?.editorialAction?.target.toLowerCase() === 'true',
      };
    },
    dynamicAttrs() {
      return {
        ...(this.booleanConversions.target && {target: '_blank', rel: 'noopener'}),
        ...(this.component?.editorialAction?.video && {['data-video']: ''}),
      };
    },
    linkUrl() {
      if (this.component?.editorialAction?.linkUrl?.includes('//')) {
        return this.component?.editorialAction?.linkUrl;
      }
      return `${this.encodedContextPath}${this.component?.editorialAction?.linkUrl}`;
    },
    linkClasses() {
      return {
        [this.component.linkStyle]: this.component?.linkStyle,
        [this.component.styleClasses]: this.component?.styleClasses,
        'button-quaternary': !this.component?.linkStyle && !this.component?.styleClasses,
      };
    },
    divClass() {
      return this.component?.divClass;
    },
    aspectRatio() {
      if (this.component?.editorialType === this.typeMap.small) {
        return '9x11';
      }
      return '4x3';
    },
    wrapperClass() {
      return this.component?.editorialType === this.typeMap.small4x3 ? 'editorial-small__wrap--4x3' : '';
    },
    smallEditorialTypes() {
      return [this.typeMap.small, this.typeMap.small4x3].some((type) => this.component.editorialType === type);
    },
  },
};
</script>
